import AuthAxios from '../axios';
import { getItem, removeItem, setItem } from './localStorage';
import { openAppOrStore } from './openSoundPillApp';

/** 보건소 QR(`/eunpyeongcare/coupon`)에 code가 없을 때 쓰는 기본값 */
export const EUNPYEONG_CARE_COUPON_CODE = 'EUNPYEONGCARE';

export const EUNPYEONG_CARE_COMPLETE_PATH = '/eunpyeongcare/complete';

export const EUNPYEONG_CARE_DEEP_LINK = 'https://limh6.app.link/3fU3hMDy75b';

export function resolveEunpyeongCareCouponCode(codeFromQuery?: string | null) {
  const trimmed = codeFromQuery?.trim();
  return trimmed ? trimmed : EUNPYEONG_CARE_COUPON_CODE;
}

const EP_LOCAL_STORAGE_KEY = 'EP_LOCAL_COUPON';

export const epLocalStorageHandler = (method: 'GET' | 'SET' | 'DELETE', coupon?: string) => {
  if (method === 'SET' && coupon) {
    setItem(EP_LOCAL_STORAGE_KEY, coupon);
  }
  if (method === 'DELETE') {
    removeItem(EP_LOCAL_STORAGE_KEY);
  }
  return getItem(EP_LOCAL_STORAGE_KEY);
};

export function getEunpyeongPostAuthPath() {
  const couponCode = epLocalStorageHandler('GET');
  return typeof couponCode === 'string' && couponCode.trim() ? EUNPYEONG_CARE_COMPLETE_PATH : null;
}

export function openEunpyeongCareApp() {
  openAppOrStore(EUNPYEONG_CARE_DEEP_LINK);
}

export type EunpyeongCouponApplyStatus = 'used' | 'registered' | 'already_registered' | 'error';

export type EunpyeongCouponApplyResult = {
  status: EunpyeongCouponApplyStatus;
  couponCode: string;
  errorCode?: string;
};

function axiosErrorMessage(error: unknown): string {
  const response = (error as { response?: { data?: { message?: string } } })?.response;
  return response?.data?.message ?? '';
}

function axiosStatus(error: unknown): number | undefined {
  const asAny = error as { response?: { status?: number }; statusCode?: number };
  return asAny?.response?.status ?? asAny?.statusCode;
}

async function fetchIsSubscribed(): Promise<boolean> {
  try {
    const { data } = await AuthAxios.get('/users/current-subsription');
    const endDate = data?.subscription?.endDate;
    return Boolean(endDate && new Date() < new Date(endDate));
  } catch {
    return false;
  }
}

async function tryUseCoupon(
  couponCode: string,
  fallback: 'registered' | 'already_registered',
): Promise<EunpyeongCouponApplyResult> {
  try {
    await AuthAxios.post('/coupon/use', { couponCode });
    return { status: 'used', couponCode };
  } catch (error) {
    const message = axiosErrorMessage(error);
    if (axiosStatus(error) === 400 || message.includes('구독')) {
      return { status: fallback, couponCode };
    }
    return { status: 'error', couponCode, errorCode: message };
  }
}

const ALREADY_REGISTERED_MESSAGES = new Set([
  'User already registered this Coupon.',
  'Registered coupon by same email.',
]);

/** 로그인 후: 등록 → 미구독이면 즉시 사용 */
export async function applyEunpyeongCareCoupon(couponCode: string): Promise<EunpyeongCouponApplyResult> {
  const isSubscribed = await fetchIsSubscribed();

  try {
    await AuthAxios.post('/coupon/register', { couponCode });
  } catch (error) {
    const message = axiosErrorMessage(error);
    if (ALREADY_REGISTERED_MESSAGES.has(message)) {
      if (!isSubscribed) {
        return tryUseCoupon(couponCode, 'already_registered');
      }
      return { status: 'already_registered', couponCode };
    }
    return { status: 'error', couponCode, errorCode: message };
  }

  if (isSubscribed) {
    return { status: 'registered', couponCode };
  }

  return tryUseCoupon(couponCode, 'registered');
}

export function mapEunpyeongCouponError(errorCode: string | undefined, t: (key: string) => string) {
  switch (errorCode) {
    case 'Coupon was not found.':
      return t('couponPage.invalidCoupon');
    case 'Coupon has expired.':
      return t('couponPage.expiredCoupon');
    case 'The coupon has exceeded the allowable usage quantity.':
      return '선착순 이용권이 마감되었습니다.';
    case 'User already registered this Coupon.':
      return t('couponPage.couponAlreadyRegistered');
    case 'Registered coupon by same email.':
      return t('couponPage.couponAlreadyRegisteredBySameEmail');
    default:
      return errorCode || t('promotion.error');
  }
}

const ALREADY_USED_ERROR_CODES = new Set([
  'User already registered this Coupon.',
  'Registered coupon by same email.',
]);

/** Figma EP_modal_already_in_Use / EP_modal_expired */
export function getEunpyeongErrorNotice(errorCode: string | undefined, t: (key: string) => string) {
  if (errorCode === 'Coupon has expired.') {
    return {
      title: '등록 기한이 만료되었습니다.',
      message: '',
    };
  }
  if (errorCode && ALREADY_USED_ERROR_CODES.has(errorCode)) {
    return {
      title: '이미 사용하신 이용권입니다.',
      message: '은평구 이용권은 계정당 1회 사용 가능합니다. 감사합니다.',
    };
  }
  return {
    title: '이용권 적용에 실패했어요',
    message: mapEunpyeongCouponError(errorCode, t),
  };
}
